import React, { useEffect, useState } from "react";
import SiteLayout from "../components/layout/SiteLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../lib/settings";
import { Loader2, Lock, Globe } from "lucide-react";
import { toast } from "sonner";

export default function PrayerPage() {
  const [form, setForm] = useState({ name: "", message: "", is_public: false, email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [publicPrayers, setPublicPrayers] = useState([]);
  const { settings } = useSettings();

  const loadPublic = () => api.get("/prayers/public").then((r) => setPublicPrayers(r.data)).catch(() => {});

  useEffect(() => { loadPublic(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please share your name and request.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/prayers", form);
      setSubmitted(true);
      setForm({ name: "", message: "", is_public: false, email: "" });
      toast.success(form.is_public ? "Posted. Awaiting pastoral review." : "Sent privately to our prayer team.");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SiteLayout>
      <section className="container-x py-20 lg:py-28">
        <div className="max-w-3xl">
          <div className="eyebrow mb-4">{settings.prayer_eyebrow}</div>
          <h1 className="display text-5xl lg:text-7xl">{settings.prayer_headline}</h1>
          <p className="mt-6 max-w-2xl text-[hsl(var(--ink-muted))] leading-relaxed">
            {settings.prayer_intro}
          </p>
        </div>
      </section>

      <section className="container-x pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <form
            data-testid="prayer-form"
            onSubmit={submit}
            className="lg:col-span-7 bg-[hsl(var(--sand))] border border-[hsl(var(--line))] p-8 lg:p-12 rounded-sm space-y-6"
          >
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Your name</label>
              <input
                data-testid="prayer-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-4 py-3 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
                placeholder="First name or initials"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Email (optional)</label>
              <input
                data-testid="prayer-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                type="email"
                className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-4 py-3 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
                placeholder="If you'd like a personal follow-up"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))]">Your request</label>
              <textarea
                data-testid="prayer-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={6}
                className="mt-2 w-full bg-[hsl(var(--linen))] border border-[hsl(var(--line))] px-4 py-3 rounded-sm focus:outline-none focus:border-[hsl(var(--terracotta))]"
                placeholder="Tell us what's on your heart…"
              />
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-muted))] mb-3">Visibility</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="prayer-visibility-private"
                  onClick={() => setForm({ ...form, is_public: false })}
                  className={`flex items-start gap-3 p-4 border rounded-sm text-left lift ${
                    !form.is_public ? "border-[hsl(var(--terracotta))] bg-[hsl(var(--linen))]" : "border-[hsl(var(--line))]"
                  }`}
                >
                  <Lock size={16} className="mt-1 text-[hsl(var(--terracotta))]" />
                  <div>
                    <div className="text-sm">Private</div>
                    <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">Only our pastoral team sees it.</div>
                  </div>
                </button>
                <button
                  type="button"
                  data-testid="prayer-visibility-public"
                  onClick={() => setForm({ ...form, is_public: true })}
                  className={`flex items-start gap-3 p-4 border rounded-sm text-left lift ${
                    form.is_public ? "border-[hsl(var(--terracotta))] bg-[hsl(var(--linen))]" : "border-[hsl(var(--line))]"
                  }`}
                >
                  <Globe size={16} className="mt-1 text-[hsl(var(--terracotta))]" />
                  <div>
                    <div className="text-sm">Share publicly</div>
                    <div className="text-xs text-[hsl(var(--ink-muted))] mt-1">Visible on the Prayer Wall after review.</div>
                  </div>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="prayer-submit"
              className="lift inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-sm bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta-deep))] text-[hsl(var(--linen))] text-sm tracking-wide disabled:opacity-60"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              Send request
            </button>

            {submitted && (
              <div data-testid="prayer-success" className="text-sm text-[hsl(var(--moss))] mt-2">
                Your request has been received. We are carrying it with you.
              </div>
            )}
          </form>

          <aside className="lg:col-span-5">
            <div className="eyebrow mb-4">{settings.prayer_wall_eyebrow}</div>
            <h2 className="display text-3xl mb-6">{settings.prayer_wall_headline}</h2>
            <p className="text-sm text-[hsl(var(--ink-muted))] leading-relaxed mb-6">
              {settings.prayer_wall_intro}
            </p>
            <div data-testid="prayer-wall" className="space-y-5">
              {publicPrayers.map((p) => (
                <div key={p.id} data-testid={`wall-item-${p.id}`} className="border-l-2 border-[hsl(var(--terracotta))] pl-5 py-1">
                  <div className="text-[hsl(var(--ink))] leading-relaxed">{p.message}</div>
                  <div className="mt-2 text-xs uppercase tracking-[0.18em] text-[hsl(var(--ink-muted))]">
                    {p.name} · {new Date(p.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
              {!publicPrayers.length && (
                <div className="text-sm text-[hsl(var(--ink-muted))]">
                  No public requests yet. Yours could be the first.
                </div>
              )}
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}
